import { useWorkspacesPageMemberWorkplacesQuery } from '../../../generated';
import type { MemberWorkspace } from '../types';
import { mapToMemberWorkspace } from '../utils';

export interface UseMemberWorkplacesResult {
  /** Null until a member is chosen and the read comes back. */
  memberWorkspace: MemberWorkspace | null;
  isLoading: boolean;
  error: Error | null;
  /** The chosen person belongs to no workspace, which is not an error. */
  notAMember: boolean;
}

/**
 * What one member's workplace selector would show them. Reading it for someone
 * else is the admin plane's power, and the only way the portal can ask this
 * question at all.
 */
export const useMemberWorkplaces = (
  workspaceId: string,
  userId: string | null
): UseMemberWorkplacesResult => {
  const read = useWorkspacesPageMemberWorkplacesQuery({
    variables: { input: { workspaceId, userId: userId ?? '' } },
    skip: !userId,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  // Deliberately not falling back to `previousData`: the panel is about one
  // person, so the last person's places on screen under a new name is wrong,
  // not merely stale. A cached member still renders at once.
  const result = read.data?.workspaceMemberViewGet;

  const resultError =
    result?.__typename === 'AuthorizationFailure' ||
    result?.__typename === 'InputValidationFailure' ||
    result?.__typename === 'WorkspaceNotFound'
      ? new Error(result.message ?? 'Their places could not be read.')
      : null;

  return {
    memberWorkspace:
      result?.__typename === 'WorkspaceMemberViewGetResult'
        ? mapToMemberWorkspace(result)
        : null,
    isLoading: read.loading,
    error: read.error ?? resultError,
    notAMember: result?.__typename === 'TeamMemberNotFound',
  };
};
